import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { PlayerImage } from './PlayerImage';
import { PlayerProfileLink } from 'widgets/statspro/js/components/common/PlayerProfileLink';

/**
 * Renders the Jumper Number table header cell.
 *
 * @param {object} props - Component props.
 * @param {object} props.cell - The react-table cell object.
 * @param {object} props.cellProps - Render props for the th element.
 * @param {boolean} props.isMainCompetition - Whether to enable profile links.
 * @param {boolean} props.isAFLW - Whether the competition is AFLW.
 * @returns {React.JSX.Element} The rendered jumper number header cell.
 */
export const JumperNumberCell = ({
    cell,
    cellProps,
    isMainCompetition,
    isAFLW
}) => {
    const hasProfileLink =
        isMainCompetition && cell?.row?.values?.player?.playerId;

    const playerImageAndNumber = (
        <>
            <PlayerImage
                photoURL={cell.value.photoURL}
                photoName={`${cell?.value?.givenName} ${cell?.value?.surname}`}
            />
            <span
                className={`mc-player-stats-table__jumper-number mc-player-stats-table__jumper-number--${cell.value.teamLocation} theme--aflc-${cell.value.teamAbbr}`}
            >
                {cell.value.jumperNumber}
            </span>
        </>
    );

    return (
        <th
            {...cellProps}
            className={classNames('stats-table__row-header-cell', {
                'stats-table__cell--sorted': cell.column.isSorted
            })}
        >
            {hasProfileLink ? (
                <PlayerProfileLink
                    classes="mc-player-stats-table__jumper-number-container stats-table__cell--link"
                    isAFLW={isAFLW}
                    player={{
                        playerId: cell.row.values.player.playerId,
                        firstName: cell?.value?.givenName,
                        surname: cell?.value?.surname
                    }}
                    clubId={cell.row.original.teamId}
                >
                    {playerImageAndNumber}
                </PlayerProfileLink>
            ) : (
                <div className="mc-player-stats-table__jumper-number-container">
                    {playerImageAndNumber}
                </div>
            )}
        </th>
    );
};

JumperNumberCell.propTypes = {
    cell: PropTypes.shape({
        id: PropTypes.string,
        value: PropTypes.shape({
            photoURL: PropTypes.string,
            givenName: PropTypes.string,
            surname: PropTypes.string,
            teamLocation: PropTypes.string,
            teamAbbr: PropTypes.string,
            jumperNumber: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number
            ])
        }).isRequired,
        column: PropTypes.shape({
            isSorted: PropTypes.bool
        }),
        row: PropTypes.shape({
            values: PropTypes.shape({
                player: PropTypes.shape({
                    playerId: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number
                    ])
                })
            }),
            original: PropTypes.shape({
                teamId: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.number
                ])
            })
        })
    }).isRequired,
    cellProps: PropTypes.object.isRequired,
    isMainCompetition: PropTypes.bool.isRequired,
    isAFLW: PropTypes.bool.isRequired
};
